import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import {
  useResponsive,
  useSafeExpoNavigation,
  useSafeFocusEffect,
  useSafeIsFocused,
  useSafeNavigation,
  useSafeRouteParams,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { LocationSearch, TransparentModal } from '../../../../components';
import { useNavigateBack } from '../../../../navigation';
import { useLocationConfirm } from '../../hooks';
import { LeftIcon, RightIcon } from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderProductSearchLocationScreen = () => {
  const { productSlug: baseProductSlug } = useSafeRouteParams<{
    productSlug?: string;
  }>();

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const isFocused = useSafeIsFocused();
  const navigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const navigateBack = useNavigateBack();

  const setOptions = navigation?.setOptions ?? expoNavigation?.setOptions;

  const handleGoBack = useCallback(() => {
    if (navigation?.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigateBack();
  }, [navigation, navigateBack]);

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const { currentBreakpoint } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const [selectedLocationData, setSelectedLocationData] = useState<SelectedLocation>();

  const { isFetchingProduct, isProductNotAvailable, handleLocationConfirm } = useLocationConfirm({
    baseProductSlug: baseProductSlug ?? '',
    restaurantId: selectedLocationData?.restaurantId,
    restaurantSlug: selectedLocationData?.restaurantSlug,
    deliveryAddressId: selectedLocationData?.deliveryOrderDetailAddressId,
    deliveryVendorName: selectedLocationData?.deliveryOrderDetailVendorName,
    deliveryVendorRestaurantId: selectedLocationData?.deliveryOrderDetailVendorRestaurantId,
    deliveryFee: selectedLocationData?.deliveryOrderDetailDeliveryFee,
  });

  const onOpenRestaurant = useCallback<OnOpenRestaurant>((context) => {
    const { activeLocation } = context;

    if (!activeLocation) return;

    const { restaurantId, restaurantSlug } = activeLocation;

    setSelectedLocationData({ restaurantId, restaurantSlug });
  }, []);

  const onOpenDeliveryRestaurant = useCallback<OnOpenDeliveryRestaurant>((context) => {
    const { deliveryLocation } = context;

    if (!deliveryLocation) return;

    const {
      restaurantId,
      restaurantSlug,
      deliveryVendorRestaurantId,
      deliveryVendorName,
      restaurantDeliveryFee,
      addressId,
    } = deliveryLocation;

    setSelectedLocationData({
      restaurantId,
      restaurantSlug,
      deliveryOrderDetailAddressId: addressId,
      deliveryOrderDetailVendorName: deliveryVendorName,
      deliveryOrderDetailVendorRestaurantId: deliveryVendorRestaurantId,
      deliveryOrderDetailDeliveryFee: restaurantDeliveryFee,
    });
  }, []);

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    if (!isExpoRouterEnabled) {
      setOptions?.({
        headerLeft: currentBreakpoint.isXS ? () => <LeftIcon onPress={handleGoBack} /> : undefined,
        headerRight: currentBreakpoint.isXS
          ? () => <RightIcon onPress={handleDismiss} />
          : undefined,
        headerShown: currentBreakpoint.isXS,
      });
    }
  }, [isExpoRouterEnabled, setOptions, currentBreakpoint.isXS, handleGoBack, handleDismiss]);

  // navigate to the selected customization screen if it's available
  useEffect(() => {
    const isProductAvailable = selectedLocationData && !isFetchingProduct;

    if (isProductAvailable) {
      void handleLocationConfirm();
    }
  }, [isFetchingProduct, isProductNotAvailable, handleLocationConfirm, selectedLocationData]);

  // Reset state when navigating away
  useSafeFocusEffect(
    useCallback(() => {
      setSelectedLocationData(undefined);
    }, []),
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!isFocused) return null;

  return (
    <TransparentModal
      testID="change-location"
      disableAnimation={currentBreakpoint.isXS}
      size="large"
      offset="10%"
      onClose={handleDismiss}
    >
      <LocationSearch
        navigateToLocation={onOpenRestaurant}
        navigateToDeliveryLocation={onOpenDeliveryRestaurant}
        interactedLocationRestaurantSlug={selectedLocationData?.restaurantSlug}
        interactedLocationDeliveryAddressId={selectedLocationData?.deliveryOrderDetailAddressId}
        isInsideModal={true}
      />
    </TransparentModal>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type OnOpenRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;

type OnOpenDeliveryRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;

type SelectedLocation = Readonly<{
  deliveryOrderDetailAddressId?: string;
  restaurantId?: string;
  restaurantSlug?: string;
  deliveryOrderDetailVendorName?: string;
  deliveryOrderDetailVendorRestaurantId?: string;
  deliveryOrderDetailDeliveryFee?: number;
}>;

type LocationSearchProps = ComponentProps<typeof LocationSearch>;
